import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '../components/Modal';

interface OutreachViewProps {
  brand: string;
}

interface HistoryFile {
  filename: string;
  date: string;
  downloadUrl: string;
  type?: string;
  contactCount?: number;
}

interface StatusData {
  today: string;
  hasUpload: boolean;
  hasEmails: boolean;
  isQueued: boolean;
  queueStatus: string | null;
  singleQueued?: boolean;
  singleReady?: boolean;
  singleFile?: string;
}

// ── Template types (mirror engine/outreach/templates.ts) ───────────────────
interface OutreachTemplate {
  id: string;
  name: string;
  description: string;
  audience: string[];
  hookStyle: string;
  emailLength: string;
  systemPromptFragment: string;
  exampleEmail1: string;
  custom?: boolean;
}

// ── Apollo types (mirror engine/outreach/apollo.ts) ────────────────────────
interface ApolloPerson {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  title?: string;
  city?: string;
  state?: string;
  country?: string;
  linkedinUrl?: string;
  company?: {
    name: string;
    domain?: string;
    industry?: string;
    employeeCount?: number;
  };
}

interface GeneratedEmail {
  '#'?: string;
  Name?: string;
  Email?: string;
  Role?: string;
  Company?: string;
  'Signal Used'?: string;
  'Subject Line'?: string;
  'Email 1 Body'?: string;
  'Email 2 Body (Day 4)'?: string;
  'Email 3 Body (Day 9)'?: string;
  'Email 4 Body (Day 16)'?: string;
  'Skip Reason'?: string;
}

interface SendResult {
  campaignId: string;
  campaignName: string;
  added: number;
  skippedAtParse: number;
  skippedByInstantly: number;
  errors?: Record<string, string>;
  dryRun?: boolean;
  wouldAdd?: number;
}

export default function OutreachView({ brand }: OutreachViewProps) {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [history, setHistory] = useState<HistoryFile[]>([]);
  const [uploading, setUploading] = useState(false);
  // #285 B6: in-app confirm replacing native window.confirm(). Promise-based
  // so call sites read `if (await askConfirm(msg)) { ... }`.
  const [confirmReq, setConfirmReq] = useState<{ message: string; resolve: (v: boolean) => void } | null>(null);
  // Drain any pending confirm (resolve false) before replacing, so a racing
  // second askConfirm can't orphan the first promise.
  const askConfirm = useCallback((message: string) => new Promise<boolean>((resolve) => setConfirmReq((prev) => { prev?.resolve(false); return { message, resolve }; })), []);
  const [generating, setGenerating] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Single-contact form
  const [singleForm, setSingleForm] = useState({ name: '', email: '', role: '', company: '', signal: '' });
  const [singleGenerating, setSingleGenerating] = useState(false);
  const [singleResult, setSingleResult] = useState<string | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<'bulk' | 'single'>('bulk');
  const [bulkSource, setBulkSource] = useState<'csv' | 'apollo'>('csv');

  // Templates (style — guides HOW the LLM writes)
  const [templates, setTemplates] = useState<OutreachTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('trigger-hook');
  const [batchPrompt, setBatchPrompt] = useState<string>('');
  // #outreach: full email sequence vs. just the email_angle personalization snippet.
  const [outputMode, setOutputMode] = useState<'full' | 'personalization'>('full');
  // #outreach: how many emails per sequence (Full mode only). 1–12, default 4.
  const [emailCount, setEmailCount] = useState<number>(4);
  const [showNewTemplateForm, setShowNewTemplateForm] = useState(false);
  const [newTemplatePrompt, setNewTemplatePrompt] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  // Sequences (industry-specific 4-email arcs + cadence + bound Instantly campaign)
  interface SequenceTemplateRow {
    id: string;
    name: string;
    industry: string;
    audience: string[];
    description: string;
    instantlyCampaign: string;
    cadenceDays: number[];
    emails: Array<{ step: number; daysFromStart: number; subject: string; body: string }>;
    anchorMode: 'llm-rewrite' | 'fixed';
    custom?: boolean;
  }
  const [sequences, setSequences] = useState<SequenceTemplateRow[]>([]);
  // null = no sequence picked (default style-template-only flow).
  // Using null rather than '' avoids JS truthiness ambiguity at call sites.
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);

  // Create-sequence form. Builds a full custom sequence (mirrors
  // sequenceTemplateSchema) and POSTs it — no LLM involved. Starts as the
  // canonical 4-email arc at Day 0 / 3 / 7 / 16; steps are add/removable.
  interface NewSeqEmail { daysFromStart: number; subject: string; body: string }
  const blankSeq = () => ({
    name: '', industry: '', audience: '', description: '', instantlyCampaign: '',
    anchorMode: 'llm-rewrite' as 'llm-rewrite' | 'fixed',
    emails: [
      { daysFromStart: 0, subject: '', body: '' },
      { daysFromStart: 3, subject: '', body: '' },
      { daysFromStart: 7, subject: '', body: '' },
      { daysFromStart: 16, subject: '', body: '' },
    ] as NewSeqEmail[],
  });
  const [showNewSequenceForm, setShowNewSequenceForm] = useState(false);
  const [creatingSequence, setCreatingSequence] = useState(false);
  const [newSeq, setNewSeq] = useState(blankSeq());

  // Apollo
  const [apolloFilters, setApolloFilters] = useState({
    titles: '',
    seniorities: '',
    employeeRanges: '',
    organizationLocations: '',
    organizationKeyword: '',
  });
  const [apolloPage, setApolloPage] = useState(1);
  const [apolloResults, setApolloResults] = useState<{ people: ApolloPerson[]; totalEntries: number; totalPages: number } | null>(null);
  const [apolloSelected, setApolloSelected] = useState<Set<string>>(new Set());
  const [apolloSearching, setApolloSearching] = useState(false);
  const [apolloImporting, setApolloImporting] = useState(false);

  // Preview + send
  const [emails, setEmails] = useState<GeneratedEmail[] | null>(null);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [sendConfirmStep, setSendConfirmStep] = useState<'idle' | 'dry-running' | 'confirm-real' | 'real-sending'>('idle');

  const fetchStatus = useCallback(() => {
    fetch(`/api/${brand}/outreach/status`).then(r => r.json()).then(setStatus).catch(() => {});
    fetch(`/api/${brand}/outreach/history`).then(r => r.json()).then(d => setHistory(d.files || [])).catch(() => {});
  }, [brand]);

  const fetchTemplates = useCallback(() => {
    fetch(`/api/${brand}/outreach/templates`)
      .then(r => r.json())
      .then(d => setTemplates(d.templates || []))
      .catch(() => {});
  }, [brand]);

  const fetchSequences = useCallback(() => {
    fetch(`/api/${brand}/outreach/sequences`)
      .then(r => r.json())
      .then(d => setSequences(d.sequences || []))
      .catch(() => {});
  }, [brand]);

  const fetchEmails = useCallback(() => {
    setLoadingEmails(true);
    fetch(`/api/${brand}/outreach/emails`)
      .then(r => r.json())
      .then(d => {
        if (d.ready && Array.isArray(d.emails)) setEmails(d.emails);
        else setEmails(null);
      })
      .catch(() => setEmails(null))
      .finally(() => setLoadingEmails(false));
  }, [brand]);

  useEffect(() => { fetchStatus(); fetchTemplates(); fetchSequences(); }, [fetchStatus, fetchTemplates, fetchSequences]);

  // Auto-load emails when status flips to hasEmails
  useEffect(() => {
    if (status?.hasEmails) fetchEmails();
    else setEmails(null);
  }, [status?.hasEmails, fetchEmails]);

  // Poll status while a batch is in flight. Also poll whenever the SERVER
  // reports a running batch (status.isQueued) even if local `generating` was
  // lost to a page reload — otherwise the preview never fills in on completion.
  useEffect(() => {
    if (!generating && !singleGenerating && !status?.isQueued) return;
    const interval = setInterval(() => {
      fetch(`/api/${brand}/outreach/status`).then(r => r.json()).then(s => {
        setStatus(s);
        // The batch is done only when the queue marker is no longer "running".
        // hasEmails flips true the instant the CSV header is written (before any
        // rows exist), so keying off it alone surfaced an empty "Emails Ready"
        // preview mid-run.
        if (s.hasEmails && !s.isQueued) {
          setGenerating(false);
          fetchEmails();
        }
        if (singleGenerating && s.singleReady) {
          setSingleGenerating(false);
          setSingleResult('ready');
          fetchStatus();
        }
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [generating, singleGenerating, status?.isQueued, brand, fetchStatus, fetchEmails]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null); setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/${brand}/outreach/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.ok) {
        setUploadResult(`Uploaded ${file.name} (${data.contactCount > 0 ? data.contactCount + ' contacts' : 'XLSX file'})`);
        fetchStatus();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGenerate = async () => {
    setGenerating(true); setError(null); setSendResult(null);
    try {
      const res = await fetch(`/api/${brand}/outreach/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: selectedTemplateId,
          sequenceId: selectedSequenceId ?? undefined,
          batchPrompt,
          outputMode,
          ...(outputMode === 'full' ? { emailCount } : {}),
        }),
      });
      const data = await res.json();
      if (!data.ok && data.error) {
        setError(data.error);
        setGenerating(false);
      }
    } catch (err) {
      setError((err as Error).message);
      setGenerating(false);
    }
  };

  const handleSingleGenerate = async () => {
    if (!singleForm.name || !singleForm.email || !singleForm.role) {
      setError('Name, Email, and Role are required'); return;
    }
    setSingleGenerating(true); setError(null); setSingleResult(null);
    try {
      const res = await fetch(`/api/${brand}/outreach/single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...singleForm,
          templateId: selectedTemplateId,
          sequenceId: selectedSequenceId ?? undefined,
          batchPrompt,
        }),
      });
      const data = await res.json();
      if (!data.ok && data.error) {
        setError(data.error);
        setSingleGenerating(false);
      }
    } catch (err) {
      setError((err as Error).message);
      setSingleGenerating(false);
    }
  };

  const handleSearchApollo = async (pageOverride?: number) => {
    setApolloSearching(true); setError(null);
    // Pass the target page explicitly instead of relying on apolloPage
    // state. setPage in the caller is async; reading apolloPage from the
    // closure here would observe the pre-update value on Prev/Next clicks.
    const targetPage = pageOverride ?? apolloPage;
    try {
      const titles = apolloFilters.titles.split(',').map(s => s.trim()).filter(Boolean);
      const seniorities = apolloFilters.seniorities.split(',').map(s => s.trim()).filter(Boolean);
      const employeeRanges = apolloFilters.employeeRanges.split(';').map(s => s.trim()).filter(Boolean);
      const orgLocations = apolloFilters.organizationLocations.split(',').map(s => s.trim()).filter(Boolean);
      const res = await fetch(`/api/${brand}/outreach/apollo/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titles: titles.length ? titles : undefined,
          seniorities: seniorities.length ? seniorities : undefined,
          employeeRanges: employeeRanges.length ? employeeRanges : undefined,
          organizationLocations: orgLocations.length ? orgLocations : undefined,
          organizationKeyword: apolloFilters.organizationKeyword || undefined,
          page: targetPage,
          perPage: 25,
        }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        setApolloResults(null);
      } else {
        setApolloResults({
          people: data.people || [],
          totalEntries: data.totalEntries || 0,
          totalPages: data.totalPages || 1,
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApolloSearching(false);
    }
  };

  const handleImportApollo = async (overwrite = false) => {
    if (!apolloResults || apolloSelected.size === 0) {
      setError('Select at least one contact first.'); return;
    }
    setApolloImporting(true); setError(null);
    try {
      const selectedPeople = apolloResults.people.filter(p => apolloSelected.has(p.id));
      const res = await fetch(`/api/${brand}/outreach/apollo/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ people: selectedPeople, overwrite }),
      });
      const data = await res.json();
      if (res.status === 409) {
        // Server refused because today's signals CSV already exists. Ask
        // the operator explicitly instead of silently overwriting — the
        // existing CSV could be a hand-curated upload from earlier today.
        if (await askConfirm(`Today's signals CSV already exists.\n\nOverwrite it with the ${selectedPeople.length} Apollo contact${selectedPeople.length === 1 ? '' : 's'} you just selected?`)) {
          setApolloImporting(false);
          await handleImportApollo(true);
        }
        return;
      }
      if (data.error) {
        setError(data.error);
      } else {
        setUploadResult(`Imported ${data.written} contacts from Apollo → ${status?.today}-signals.csv`);
        setApolloSelected(new Set());
        fetchStatus();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApolloImporting(false);
    }
  };

  const handleSendToInstantly = async (realSend: boolean) => {
    setSending(true); setError(null);
    if (!realSend) setSendConfirmStep('dry-running');
    else setSendConfirmStep('real-sending');
    try {
      const res = await fetch(`/api/${brand}/outreach/send-to-instantly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dryRun: !realSend,
          // When a sequence is picked, the server uses its bound Instantly
          // campaign (e.g. "ZopNight FMCG") instead of the global default,
          // and PATCHes the cadence to match before pushing leads.
          sequenceId: selectedSequenceId ?? undefined,
        }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        setSendConfirmStep('idle');
      } else {
        setSendResult(data);
        if (realSend) setSendConfirmStep('idle');
        else setSendConfirmStep('confirm-real');
      }
    } catch (err) {
      setError((err as Error).message);
      setSendConfirmStep('idle');
    } finally {
      setSending(false);
    }
  };

  const handleCreateTemplateFromPrompt = async () => {
    if (!newTemplatePrompt.trim()) { setError('Enter a prompt describing the template.'); return; }
    setCreatingTemplate(true); setError(null);
    try {
      const res = await fetch(`/api/${brand}/outreach/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromPrompt: newTemplatePrompt, name: newTemplateName || undefined }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        await fetchTemplates();
        setSelectedTemplateId(data.template.id);
        setShowNewTemplateForm(false);
        setNewTemplatePrompt(''); setNewTemplateName('');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!await askConfirm(`Delete custom template "${id}"? This can't be undone.`)) return;
    try {
      const res = await fetch(`/api/${brand}/outreach/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) setError(data.error);
      else { await fetchTemplates(); if (selectedTemplateId === id) setSelectedTemplateId('trigger-hook'); }
    } catch (err) { setError((err as Error).message); }
  };

  // ── Sequence create / delete ────────────────────────────────────────────

  const updateSeqEmail = (i: number, field: keyof NewSeqEmail, value: number | string) =>
    setNewSeq(p => ({ ...p, emails: p.emails.map((e, j) => (j === i ? { ...e, [field]: value } : e)) }));
  const addSeqEmail = () =>
    setNewSeq(p => ({
      ...p,
      emails: [...p.emails, { daysFromStart: (p.emails[p.emails.length - 1]?.daysFromStart ?? 0) + 7, subject: '', body: '' }],
    }));
  const removeSeqEmail = (i: number) =>
    setNewSeq(p => ({ ...p, emails: p.emails.filter((_, j) => j !== i) }));

  const slugifySeqId = (name: string) => {
    let id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    if (!/^[a-z]/.test(id)) id = `seq-${id}`.slice(0, 40);
    return id.replace(/-+$/g, '') || 'sequence';
  };

  const handleCreateSequence = async () => {
    // Validate client-side against sequenceTemplateSchema so the operator gets
    // an inline error instead of a 422 round-trip. step/cadenceDays are derived
    // from the email rows, so the schema's cross-field refinements hold by build.
    const audience = newSeq.audience.split(',').map(s => s.trim()).filter(Boolean);
    const emails = newSeq.emails.map((e, i) => ({
      step: i + 1,
      daysFromStart: Number(e.daysFromStart),
      subject: e.subject.trim(),
      body: e.body.trim(),
    }));
    const cadenceDays = emails.map(e => e.daysFromStart);

    if (newSeq.name.trim().length < 2) { setError('Sequence name is required (min 2 chars).'); return; }
    if (newSeq.industry.trim().length < 2) { setError('Industry is required (min 2 chars).'); return; }
    if (newSeq.description.trim().length < 10) { setError('Description must be at least 10 characters.'); return; }
    if (newSeq.instantlyCampaign.trim().length < 2) { setError('Instantly campaign name is required.'); return; }
    if (audience.length < 1) { setError('Add at least one audience tag (comma-separated).'); return; }
    if (emails.length < 1) { setError('Add at least one email step.'); return; }
    if (cadenceDays[0] !== 0) { setError('Step 1 must be Day 0 (it fires at enrollment time).'); return; }
    // Cadence must strictly increase, else Instantly fires steps out of order.
    for (let i = 1; i < cadenceDays.length; i++) {
      if (cadenceDays[i] <= cadenceDays[i - 1]) {
        setError(`Step ${i + 1} (Day ${cadenceDays[i]}) must fire later than step ${i} (Day ${cadenceDays[i - 1]}).`); return;
      }
    }
    const id = slugifySeqId(newSeq.name);
    if (!/^[a-z][a-z0-9-]{1,40}$/.test(id)) {
      setError('Could not derive a valid id from the name — use at least 2 letters or digits.'); return;
    }
    for (const [i, e] of emails.entries()) {
      if (e.subject.length < 3) { setError(`Step ${i + 1}: subject must be at least 3 characters.`); return; }
      if (e.body.length < 50) { setError(`Step ${i + 1}: body must be at least 50 characters.`); return; }
    }

    setCreatingSequence(true); setError(null);
    try {
      const template = {
        id,
        name: newSeq.name.trim(),
        industry: newSeq.industry.trim(),
        audience,
        description: newSeq.description.trim(),
        instantlyCampaign: newSeq.instantlyCampaign.trim(),
        cadenceDays,
        emails,
        anchorMode: newSeq.anchorMode,
      };
      const res = await fetch(`/api/${brand}/outreach/sequences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        await fetchSequences();
        setSelectedSequenceId(data.sequence.id);
        setShowNewSequenceForm(false);
        setNewSeq(blankSeq());
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreatingSequence(false);
    }
  };

  const handleDeleteSequence = async (id: string) => {
    if (!await askConfirm(`Delete custom sequence "${id}"? This can't be undone.`)) return;
    try {
      const res = await fetch(`/api/${brand}/outreach/sequences/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) setError(data.error);
      else { await fetchSequences(); if (selectedSequenceId === id) setSelectedSequenceId(null); }
    } catch (err) { setError((err as Error).message); }
  };

  // ── Styles ────────────────────────────────────────────────────────────

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border)',
    borderRadius: 0, fontSize: '0.9rem', background: 'var(--bg-primary)',
    color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)',
    marginBottom: '0.25rem', display: 'block',
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <main className="main-content" style={{ padding: '2rem' }}>
      <Modal open={!!confirmReq} onClose={() => { confirmReq?.resolve(false); setConfirmReq(null); }} title="Please confirm" maxWidth={440}>
        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 16px', whiteSpace: 'pre-wrap', color: 'var(--text-primary)', fontSize: 'var(--fs-body)', lineHeight: 1.5 }}>{confirmReq?.message}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => { confirmReq?.resolve(false); setConfirmReq(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={() => { confirmReq?.resolve(true); setConfirmReq(null); }}>Confirm</button>
          </div>
        </div>
      </Modal>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>Cold Outreach Emails</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
        Pull contacts from Apollo or upload a CSV, pick a template, generate, preview, and send via Instantly.
      </p>

      {/* Status Banner */}
      {status && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <StatusPill label="Today" value={status.today} />
          <StatusPill label="Signals" value={status.hasUpload ? 'Loaded' : 'None'} color={status.hasUpload ? 'var(--success)' : 'var(--text-tertiary)'} />
          <StatusPill label="Emails" value={status.hasEmails ? 'Ready' : status.isQueued || generating ? 'Generating...' : 'Not generated'} color={status.hasEmails ? 'var(--success)' : status.isQueued || generating ? 'var(--warning)' : 'var(--text-tertiary)'} />
        </div>
      )}

      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid var(--border)' }}>
        <TabBtn active={activeTab === 'bulk'} onClick={() => setActiveTab('bulk')}>Bulk Pipeline</TabBtn>
        <TabBtn active={activeTab === 'single'} onClick={() => setActiveTab('single')}>Quick Add (Single Contact)</TabBtn>
      </div>

      {/* ── BULK TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'bulk' && (
        <>
          {/* Source picker */}
          <section style={sectionBoxStyle}>
            <div style={{ display: 'flex', gap: '0', marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
              <SubTabBtn active={bulkSource === 'csv'} onClick={() => setBulkSource('csv')}>📂 Upload CSV</SubTabBtn>
              <SubTabBtn active={bulkSource === 'apollo'} onClick={() => setBulkSource('apollo')}>🔍 Pick from Apollo</SubTabBtn>
            </div>

            {bulkSource === 'csv' && (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx" onChange={handleUpload} style={{ display: 'none' }} />
                <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                  {uploading ? 'Uploading...' : 'Drop your Signals CSV / XLSX here'}
                </p>
                <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Any CSV with contact + signal columns. Schema auto-detected.
                </p>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={primaryBtn()}>
                  {uploading ? 'Uploading...' : 'Choose File'}
                </button>
                {uploadResult && <p style={{ color: 'var(--success)', marginTop: '0.75rem', fontWeight: 500 }}>{uploadResult}</p>}
              </div>
            )}

            {bulkSource === 'apollo' && (
              <ApolloPicker
                filters={apolloFilters} setFilters={setApolloFilters}
                results={apolloResults} selected={apolloSelected} setSelected={setApolloSelected}
                searching={apolloSearching} importing={apolloImporting}
                page={apolloPage} setPage={setApolloPage}
                onSearch={handleSearchApollo} onImport={handleImportApollo}
                inputStyle={inputStyle} labelStyle={labelStyle}
              />
            )}
          </section>

          {/* Sequence picker — pick an industry-specific 4-email arc with cadence,
              bound to an Instantly campaign. Optional; when unset, the LLM uses
              the style template alone (1-email-at-a-time generation). */}
          {status?.hasUpload && (
            <section style={sectionBoxStyle}>
              <h3 style={{ fontWeight: 700, margin: '0 0 0.25rem', fontSize: '1rem' }}>Pick a sequence (optional)</h3>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                Each sequence carries 4 anchor emails + per-step cadence (Day 0 / Day 3 / Day 7 / etc.) + a bound Instantly campaign. The LLM rewrites the 4 bodies per lead; Instantly fires them on the cadence.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button
                  onClick={() => setSelectedSequenceId(null)}
                  style={{
                    padding: '0.4rem 0.8rem',
                    border: '1px solid',
                    borderColor: !selectedSequenceId ? 'var(--accent)' : 'var(--border)',
                    background: !selectedSequenceId ? 'var(--accent)' : 'var(--bg-primary)',
                    color: !selectedSequenceId ? '#fff' : 'var(--text-primary)',
                    borderRadius: 0, fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  No sequence (style template only)
                </button>
                {/* Only operator-created (custom) sequences are listed. Built-in
                    sample sequences (e.g. the FMCG seed) are hidden from the picker
                    by design — create your own with the button below. */}
                {sequences.filter(s => s.custom).map(s => (
                  // Chip is a non-interactive <span> wrapping two real <button>s
                  // (select + delete) as siblings. Never nest an interactive
                  // control inside a <button> — invalid HTML and AT-hostile.
                  <span
                    key={s.id}
                    title={`${s.industry} · cadence ${s.cadenceDays.join('/')}d · → ${s.instantlyCampaign}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.4rem 0.8rem',
                      border: '1px solid',
                      borderColor: selectedSequenceId === s.id ? 'var(--accent)' : 'var(--border)',
                      background: selectedSequenceId === s.id ? 'var(--accent)' : 'var(--bg-primary)',
                      color: selectedSequenceId === s.id ? '#fff' : 'var(--text-primary)',
                      borderRadius: 0, fontSize: '0.85rem', fontWeight: 500,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedSequenceId(s.id)}
                      style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
                    >
                      {s.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSequence(s.id)}
                      aria-label={`Delete ${s.id}`}
                      style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, opacity: 0.6, fontSize: '0.7rem', color: 'inherit', cursor: 'pointer' }}
                    >✕</button>
                  </span>
                ))}
                <button
                  onClick={() => setShowNewSequenceForm(v => !v)}
                  style={{ padding: '0.4rem 0.8rem', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-secondary)', borderRadius: 0, fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
                >
                  + Create a sequence
                </button>
              </div>
              {selectedSequenceId && (() => {
                const seq = sequences.find(s => s.id === selectedSequenceId);
                if (!seq) return null;
                return (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Cadence: <strong>{seq.cadenceDays.map(d => `D${d}`).join(' · ')}</strong>{' · '}
                    Campaign: <strong>{seq.instantlyCampaign}</strong>{' · '}
                    Mode: <strong>{seq.anchorMode === 'fixed' ? 'fixed (no LLM rewrite)' : 'LLM-rewrite per lead'}</strong>
                    <details style={{ marginTop: '0.5rem' }}>
                      <summary style={{ cursor: 'pointer' }}>Preview {seq.emails.length} anchor emails</summary>
                      {seq.emails.map(e => (
                        <div key={e.step} style={{ marginTop: '0.5rem' }}>
                          <p style={{ margin: 0, fontWeight: 600 }}>Step {e.step} (Day {e.daysFromStart}) — {e.subject}</p>
                          <pre style={examplePreStyle}>{e.body}</pre>
                        </div>
                      ))}
                    </details>
                  </div>
                );
              })()}
            </section>
          )}

          {/* Create-sequence form — builds a full custom sequence and POSTs it to
              /outreach/sequences (saveCustomSequence). No LLM needed; it mirrors
              sequenceTemplateSchema. step + cadenceDays are derived from the rows. */}
          {status?.hasUpload && showNewSequenceForm && (
            <section style={{ ...sectionBoxStyle, background: 'var(--warning-light)', borderColor: 'var(--warning)' }}>
              <h3 style={{ fontWeight: 700, margin: '0 0 0.25rem', fontSize: '1rem' }}>Create a sequence</h3>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem', margin: '0 0 1rem' }}>
                A reusable email arc bound to an Instantly campaign. Step 1 fires at Day 0; each later step fires N days from enrollment. In LLM-rewrite mode each body is rewritten per lead; in fixed mode it is sent verbatim with only placeholder substitution.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div><label htmlFor="seq-name" style={labelStyle}>Name *</label><input id="seq-name" style={inputStyle} value={newSeq.name} onChange={e => setNewSeq(p => ({ ...p, name: e.target.value }))} placeholder="e.g. SaaS · Multi-cloud cost" /></div>
                <div><label htmlFor="seq-industry" style={labelStyle}>Industry *</label><input id="seq-industry" style={inputStyle} value={newSeq.industry} onChange={e => setNewSeq(p => ({ ...p, industry: e.target.value }))} placeholder="e.g. SaaS" /></div>
                <div><label htmlFor="seq-instantly-campaign" style={labelStyle}>Instantly campaign *</label><input id="seq-instantly-campaign" style={inputStyle} value={newSeq.instantlyCampaign} onChange={e => setNewSeq(p => ({ ...p, instantlyCampaign: e.target.value }))} placeholder="exact campaign name in Instantly" /></div>
                <div>
                  <label htmlFor="seq-anchor-mode" style={labelStyle}>Anchor mode</label>
                  <select id="seq-anchor-mode" style={inputStyle} value={newSeq.anchorMode} onChange={e => setNewSeq(p => ({ ...p, anchorMode: e.target.value as 'llm-rewrite' | 'fixed' }))}>
                    <option value="llm-rewrite">LLM rewrite per lead</option>
                    <option value="fixed">Fixed (verbatim + placeholder fill)</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}><label htmlFor="seq-audience" style={labelStyle}>Audience tags * (comma-separated)</label><input id="seq-audience" style={inputStyle} value={newSeq.audience} onChange={e => setNewSeq(p => ({ ...p, audience: e.target.value }))} placeholder="e.g. cto, vp engineering, head of platform" /></div>
                <div style={{ gridColumn: '1 / -1' }}><label htmlFor="seq-description" style={labelStyle}>Description *</label><input id="seq-description" style={inputStyle} value={newSeq.description} onChange={e => setNewSeq(p => ({ ...p, description: e.target.value }))} placeholder="One line on who this is for and the angle (min 10 chars)" /></div>
              </div>

              <h4 style={{ fontWeight: 600, margin: '1.25rem 0 0.5rem', fontSize: '0.9rem' }}>Email steps</h4>
              {newSeq.emails.map((em, i) => (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 0, padding: '0.75rem', marginBottom: '0.75rem', background: 'var(--bg-primary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <strong style={{ fontSize: '0.85rem' }}>Step {i + 1}</strong>
                    <label htmlFor={`seq-email-day-${i}`} style={{ ...labelStyle, margin: 0 }}>Day</label>
                    <input
                      id={`seq-email-day-${i}`}
                      type="number" min={0} value={em.daysFromStart} disabled={i === 0}
                      onChange={e => updateSeqEmail(i, 'daysFromStart', Number(e.target.value))}
                      style={{ ...inputStyle, width: '90px' }}
                      title={i === 0 ? 'Step 1 always fires at Day 0' : 'Days from enrollment'}
                    />
                    {i === 0 && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>(fixed at Day 0)</span>}
                    {newSeq.emails.length > 1 && (
                      <button onClick={() => removeSeqEmail(i)} style={{ ...secondaryBtn(), marginLeft: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}>Remove</button>
                    )}
                  </div>
                  <input style={{ ...inputStyle, marginBottom: '0.5rem' }} value={em.subject} onChange={e => updateSeqEmail(i, 'subject', e.target.value)} placeholder="Subject line (min 3 chars)" />
                  <textarea style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} rows={3} value={em.body} onChange={e => updateSeqEmail(i, 'body', e.target.value)} placeholder="Body (min 50 chars). You can use {firstName} / {{company}} placeholders — preserved per lead." />
                </div>
              ))}
              <button onClick={addSeqEmail} style={{ ...secondaryBtn(), marginBottom: '1rem' }}>+ Add step</button>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleCreateSequence} disabled={creatingSequence} style={primaryBtn()}>
                  {creatingSequence ? 'Saving...' : 'Create sequence'}
                </button>
                <button onClick={() => { setShowNewSequenceForm(false); setNewSeq(blankSeq()); }} style={secondaryBtn()}>Cancel</button>
              </div>
            </section>
          )}

          {/* Template + batch prompt — shown when signals exist */}
          {status?.hasUpload && (
            <section style={sectionBoxStyle}>
              <h3 style={{ fontWeight: 700, margin: '0 0 1rem', fontSize: '1rem' }}>Pick a style template + tone</h3>
              <TemplatePicker
                templates={templates}
                selectedId={selectedTemplateId}
                onSelect={setSelectedTemplateId}
                onDelete={handleDeleteTemplate}
                onCreateNew={() => setShowNewTemplateForm(true)}
              />
              {selectedTemplate && (
                <details style={{ marginTop: '0.75rem' }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    Preview example for "{selectedTemplate.name}"
                  </summary>
                  <pre style={examplePreStyle}>{selectedTemplate.exampleEmail1}</pre>
                </details>
              )}

              <label htmlFor="bulk-batch-prompt" style={{ ...labelStyle, marginTop: '1rem' }}>Per-batch guidance (optional)</label>
              <textarea
                id="bulk-batch-prompt"
                value={batchPrompt}
                onChange={e => setBatchPrompt(e.target.value)}
                placeholder='e.g. "Lead with the new EU Data Act angle. Keep it short. Reference the recent multi-cloud audit news in line 1."'
                rows={3}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
                Adds extra angle / tone instructions on top of the template, for this batch only. Max ~1500 chars.
              </p>
            </section>
          )}

          {/* New-template-from-prompt mini-form */}
          {showNewTemplateForm && (
            <section style={{ ...sectionBoxStyle, background: 'var(--warning-light)', borderColor: 'var(--warning)' }}>
              <h3 style={{ fontWeight: 700, margin: '0 0 1rem', fontSize: '1rem' }}>Create new template from a prompt</h3>
              <label htmlFor="new-template-name" style={labelStyle}>Template name (optional)</label>
              <input id="new-template-name" style={inputStyle} value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} placeholder='e.g. "Series-B Founders"' />
              <label htmlFor="new-template-prompt" style={{ ...labelStyle, marginTop: '0.75rem' }}>Describe the template</label>
              <textarea
                id="new-template-prompt"
                value={newTemplatePrompt}
                onChange={e => setNewTemplatePrompt(e.target.value)}
                placeholder='e.g. "For Series-B SaaS founders feeling board pressure on cloud costs. Conversational tone. Lead with a contrarian stat about AWS waste in their stage. Close with a low-friction question."'
                rows={4}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleCreateTemplateFromPrompt} disabled={creatingTemplate} style={primaryBtn()}>
                  {creatingTemplate ? 'Generating template...' : 'Create with LLM'}
                </button>
                <button onClick={() => { setShowNewTemplateForm(false); setNewTemplatePrompt(''); setNewTemplateName(''); }} style={secondaryBtn()}>Cancel</button>
              </div>
            </section>
          )}

          {/* Generate button */}
          {status?.hasUpload && !status?.isQueued && !status?.hasEmails && (
            <section style={{ marginBottom: '2rem' }}>
              {/* #outreach: choose what to generate. Both modes produce the
                  Instantly CSV (email_angle = personalization); "Full" also
                  writes the subject + email bodies for review / the API push. */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {([
                  ['full', '📧 Full emails', 'Subject + email sequence + personalization'],
                  ['personalization', '✦ Personalization only', 'Just the email_angle snippet — cheaper/faster'],
                ] as const).map(([mode, label, hint]) => (
                  <button
                    key={mode}
                    onClick={() => setOutputMode(mode)}
                    title={hint}
                    style={{
                      flex: 1, padding: '0.6rem 0.75rem', borderRadius: 0, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${outputMode === mode ? 'var(--accent)' : 'var(--border)'}`,
                      background: outputMode === mode ? 'var(--accent)' : 'var(--bg-primary)',
                      color: outputMode === mode ? '#fff' : 'var(--text-primary)',
                    }}
                  >
                    <div>{label}</div>
                    <div style={{ fontWeight: 400, fontSize: '0.72rem', opacity: 0.85, marginTop: 2 }}>{hint}</div>
                  </button>
                ))}
              </div>
              {outputMode === 'full' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <label htmlFor="email-count" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Emails per sequence:</label>
                  <input
                    id="email-count"
                    type="number" min={1} max={12} value={emailCount}
                    onChange={(e) => setEmailCount(Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 4)))}
                    style={{ width: 64, padding: '0.35rem 0.5rem', borderRadius: 0, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>1–12 (one body column per email)</span>
                </div>
              )}
              <button onClick={handleGenerate} disabled={generating} style={{ ...primaryBtn(), width: '100%', padding: '0.75rem 2rem', fontSize: '1rem' }}>
                {generating
                  ? '⏳ Generating... (3-5 min)'
                  : outputMode === 'personalization' ? '✦ Generate Personalizations' : `📧 Generate ${emailCount}-Email Sequence`}
              </button>
            </section>
          )}

          {/* Generating indicator — shown for the whole run. Gated on isQueued
              (the running marker), NOT !hasEmails, since hasEmails is true from
              the moment the header is written. */}
          {(generating || status?.isQueued) && (
            <section style={{ ...sectionBoxStyle, background: 'var(--warning-light)', borderColor: 'var(--warning)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.25rem' }}>⏳</span>
                <div>
                  <p style={{ fontWeight: 600, margin: 0, color: 'var(--text)' }}>Generating outreach emails...</p>
                  <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                    Writing personalised 4-email sequences using the "{selectedTemplate?.name || selectedTemplateId}" template.
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Preview + Send — only once the batch has finished (isQueued false),
              so a half-written CSV never shows as a complete, sendable preview. */}
          {status?.hasEmails && !status?.isQueued && (
            <EmailPreview
              emails={emails}
              loading={loadingEmails}
              brand={brand}
              today={status.today}
              sending={sending}
              sendResult={sendResult}
              sendConfirmStep={sendConfirmStep}
              onSendDryRun={() => handleSendToInstantly(false)}
              onSendReal={() => handleSendToInstantly(true)}
              onCancelConfirm={() => { setSendConfirmStep('idle'); setSendResult(null); }}
              onReload={() => fetchEmails()}
            />
          )}
        </>
      )}

      {/* ── SINGLE-CONTACT TAB ──────────────────────────────────────── */}
      {activeTab === 'single' && (
        <section style={sectionBoxStyle}>
          <h3 style={{ fontWeight: 700, margin: '0 0 1rem', fontSize: '1rem' }}>Generate email sequence for a single contact</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div><label htmlFor="single-name" style={labelStyle}>Name *</label><input id="single-name" style={inputStyle} placeholder="e.g. Rajat Jain" value={singleForm.name} onChange={e => setSingleForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label htmlFor="single-email" style={labelStyle}>Email *</label><input id="single-email" style={inputStyle} placeholder="e.g. rajat@flipkart.com" value={singleForm.email} onChange={e => setSingleForm(p => ({ ...p, email: e.target.value }))} /></div>
            <div><label htmlFor="single-role" style={labelStyle}>Role / Title *</label><input id="single-role" style={inputStyle} placeholder="e.g. Sr. Director" value={singleForm.role} onChange={e => setSingleForm(p => ({ ...p, role: e.target.value }))} /></div>
            <div><label htmlFor="single-company" style={labelStyle}>Company (optional)</label><input id="single-company" style={inputStyle} placeholder="e.g. Flipkart" value={singleForm.company} onChange={e => setSingleForm(p => ({ ...p, company: e.target.value }))} /></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="single-signal" style={labelStyle}>Signal / Trigger Event (optional)</label>
              <input id="single-signal" style={inputStyle} placeholder="e.g. IPO filed + hybrid multi-cloud cost allocation" value={singleForm.signal} onChange={e => setSingleForm(p => ({ ...p, signal: e.target.value }))} />
            </div>
          </div>

          <div style={{ marginTop: '1.25rem' }}>
            <h4 style={{ fontWeight: 600, margin: '0 0 0.5rem', fontSize: '0.9rem' }}>Template + tone</h4>
            <TemplatePicker templates={templates} selectedId={selectedTemplateId} onSelect={setSelectedTemplateId} onDelete={handleDeleteTemplate} onCreateNew={() => setShowNewTemplateForm(true)} />
            <label htmlFor="single-batch-prompt" style={{ ...labelStyle, marginTop: '0.75rem' }}>Per-batch guidance (optional)</label>
            <textarea id="single-batch-prompt" value={batchPrompt} onChange={e => setBatchPrompt(e.target.value)} rows={2} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          </div>

          <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={handleSingleGenerate} disabled={singleGenerating} style={primaryBtn()}>
              {singleGenerating ? '⏳ Generating...' : 'Generate Email Sequence'}
            </button>
            {singleGenerating && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Writing 4-email sequence...</span>}
          </div>

          {singleResult === 'ready' && status?.singleFile && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--success-light)', border: '1px solid var(--success)', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontWeight: 600, margin: 0, color: 'var(--success)' }}>Email sequence ready for {singleForm.name}.</p>
              <a href={`/api/${brand}/outreach/download/${status.singleFile}`} download style={{ background: 'var(--success)', color: '#fff', borderRadius: 0, padding: '0.5rem 1.25rem', fontWeight: 600, textDecoration: 'none', fontSize: '0.85rem' }}>Download CSV</a>
            </div>
          )}
        </section>
      )}

      {/* Error display */}
      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '1rem', padding: '0.75rem', background: 'var(--danger-light)', borderRadius: 0, border: '1px solid var(--danger)' }}>
          {error}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <section>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>History</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '2px solid var(--border)' }}><th style={{ textAlign: 'left', padding: '0.5rem 0', fontWeight: 600 }}>Date</th><th style={{ textAlign: 'left', padding: '0.5rem 0', fontWeight: 600 }}>File</th><th style={{ textAlign: 'right', padding: '0.5rem 0', fontWeight: 600 }}>Download</th></tr></thead>
            <tbody>{history.map(h => (
              <tr key={h.filename} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0' }}>{h.date}</td>
                <td style={{ padding: '0.5rem 0', color: 'var(--text-secondary)' }}>{h.filename}</td>
                <td style={{ padding: '0.5rem 0', textAlign: 'right' }}><a href={h.downloadUrl} download style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Download</a></td>
              </tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </main>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function TemplatePicker({ templates, selectedId, onSelect, onDelete, onCreateNew }: {
  templates: OutreachTemplate[]; selectedId: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onCreateNew: () => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {templates.map(t => (
          // Same chip pattern as the sequence picker: non-interactive <span>
          // wrapper, real <button>s inside (select + optional delete).
          <span
            key={t.id}
            title={t.description}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.4rem 0.8rem',
              border: '1px solid',
              borderColor: selectedId === t.id ? 'var(--accent)' : 'var(--border)',
              background: selectedId === t.id ? 'var(--accent)' : 'var(--bg-primary)',
              color: selectedId === t.id ? '#fff' : 'var(--text-primary)',
              borderRadius: 0,
              fontSize: '0.85rem',
              fontWeight: 500,
            }}
          >
            <button
              type="button"
              onClick={() => onSelect(t.id)}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
            >
              {t.name}
            </button>
            {t.custom && (
              <button
                type="button"
                onClick={() => onDelete(t.id)}
                aria-label={`Delete ${t.id}`}
                style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, opacity: 0.6, fontSize: '0.7rem', color: 'inherit', cursor: 'pointer' }}
              >✕</button>
            )}
          </span>
        ))}
        <button onClick={onCreateNew} style={{ padding: '0.4rem 0.8rem', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-secondary)', borderRadius: 0, fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
          + New from prompt
        </button>
      </div>
    </div>
  );
}

function ApolloPicker({ filters, setFilters, results, selected, setSelected, searching, importing, page, setPage, onSearch, onImport, inputStyle, labelStyle }: {
  filters: { titles: string; seniorities: string; employeeRanges: string; organizationLocations: string; organizationKeyword: string };
  setFilters: React.Dispatch<React.SetStateAction<{ titles: string; seniorities: string; employeeRanges: string; organizationLocations: string; organizationKeyword: string }>>;
  results: { people: ApolloPerson[]; totalEntries: number; totalPages: number } | null;
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  searching: boolean;
  importing: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  /** Optional pageOverride lets the caller request a specific page
   *  without first round-tripping through React's async setState. */
  onSearch: (pageOverride?: number) => void;
  onImport: () => void;
  inputStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
}) {
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (!results) return;
    setSelected(prev => {
      if (prev.size === results.people.length) return new Set();
      return new Set(results.people.map(p => p.id));
    });
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div>
          <label htmlFor="apollo-titles" style={labelStyle}>Titles (comma-separated)</label>
          <input id="apollo-titles" style={inputStyle} placeholder="cto, vp engineering, head of platform" value={filters.titles} onChange={e => setFilters(p => ({ ...p, titles: e.target.value }))} />
        </div>
        <div>
          <label htmlFor="apollo-seniorities" style={labelStyle}>Seniorities</label>
          <input id="apollo-seniorities" style={inputStyle} placeholder="c_suite, vp, director" value={filters.seniorities} onChange={e => setFilters(p => ({ ...p, seniorities: e.target.value }))} />
        </div>
        <div>
          <label htmlFor="apollo-employee-ranges" style={labelStyle}>Employee ranges (semicolon-separated)</label>
          <input id="apollo-employee-ranges" style={inputStyle} placeholder="100,500; 500,1000" value={filters.employeeRanges} onChange={e => setFilters(p => ({ ...p, employeeRanges: e.target.value }))} />
        </div>
        <div>
          <label htmlFor="apollo-locations" style={labelStyle}>Locations</label>
          <input id="apollo-locations" style={inputStyle} placeholder="United States, India" value={filters.organizationLocations} onChange={e => setFilters(p => ({ ...p, organizationLocations: e.target.value }))} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="apollo-org-keyword" style={labelStyle}>Company keyword (optional)</label>
          <input id="apollo-org-keyword" style={inputStyle} placeholder="multi-cloud, kubernetes, fintech" value={filters.organizationKeyword} onChange={e => setFilters(p => ({ ...p, organizationKeyword: e.target.value }))} />
        </div>
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button onClick={() => { setPage(1); onSearch(1); }} disabled={searching} style={primaryBtn()}>
          {searching ? 'Searching...' : 'Search Apollo'}
        </button>
        {results && (
          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
            {results.totalEntries} total · showing {results.people.length} · page {page} / {results.totalPages}
          </span>
        )}
      </div>

      {results && results.people.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <button onClick={toggleAll} style={{ ...secondaryBtn(), padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>
              {selected.size === results.people.length ? 'Clear all' : 'Select all on page'}
            </button>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{selected.size} selected</span>
          </div>
          <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ background: 'var(--bg-secondary)' }}>
                <tr>
                  <th style={{ width: '32px', padding: '0.5rem' }}></th>
                  <th style={apolloThStyle}>Name</th>
                  <th style={apolloThStyle}>Title</th>
                  <th style={apolloThStyle}>Company</th>
                  <th style={apolloThStyle}>Email</th>
                </tr>
              </thead>
              <tbody>
                {results.people.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{p.fullName}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{p.title ?? '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{p.company?.name ?? '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: p.email ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{p.email ?? '(locked)'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); onSearch(p); }} disabled={page <= 1 || searching} style={secondaryBtn()}>← Prev</button>
            <button onClick={() => { const p = page + 1; setPage(p); onSearch(p); }} disabled={page >= results.totalPages || searching} style={secondaryBtn()}>Next →</button>
            <button onClick={onImport} disabled={importing || selected.size === 0} style={{ ...primaryBtn(), marginLeft: 'auto' }}>
              {importing ? 'Importing...' : `Import ${selected.size} → Signals CSV`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailPreview({ emails, loading, brand, today, sending, sendResult, sendConfirmStep, onSendDryRun, onSendReal, onCancelConfirm, onReload }: {
  emails: GeneratedEmail[] | null;
  loading: boolean;
  brand: string;
  today: string;
  sending: boolean;
  sendResult: SendResult | null;
  sendConfirmStep: 'idle' | 'dry-running' | 'confirm-real' | 'real-sending';
  onSendDryRun: () => void;
  onSendReal: () => void;
  onCancelConfirm: () => void;
  onReload: () => void;
}) {
  const sendable = emails?.filter(e => !(e['Skip Reason'] && e['Skip Reason'].length > 0)) || [];

  return (
    <section style={{ ...sectionBoxStyle, background: 'var(--success-light)', borderColor: 'var(--success)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h3 style={{ fontWeight: 700, margin: 0, color: 'var(--success)' }}>Emails Ready</h3>
          <p style={{ color: 'var(--success)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
            {sendable.length} sendable · {(emails?.length ?? 0) - sendable.length} skipped
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <a href={`/api/${brand}/outreach/download/${today}-emails.csv`} download style={{ ...secondaryBtn(), textDecoration: 'none' }}>Download CSV</a>
          {sendConfirmStep === 'idle' && (
            <button onClick={onSendDryRun} disabled={sending} style={{ background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 0, padding: '0.6rem 1.25rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              {sending ? 'Validating...' : '📨 Validate + preview send'}
            </button>
          )}
        </div>
      </div>

      {/* Send confirmation flow */}
      {sendConfirmStep === 'confirm-real' && sendResult && (
        <div style={{ padding: '1rem', background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 0, marginBottom: '1rem' }}>
          <p style={{ fontWeight: 700, margin: '0 0 0.5rem', color: 'var(--text)' }}>⚠ Confirm real send to Instantly</p>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
            Dry-run validated. Campaign: <strong>{sendResult.campaignName}</strong> · Will add: <strong>{sendResult.wouldAdd}</strong> leads.
          </p>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 0.75rem', fontSize: '0.85rem' }}>
            Clicking <em>Send for real</em> below will push leads to Instantly, which will start sending real emails to these contacts via the configured sequence. This is not reversible from this UI.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onSendReal} disabled={sending} style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 0, padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              {sending ? 'Sending...' : '🚀 Send for real'}
            </button>
            <button onClick={onCancelConfirm} style={secondaryBtn()}>Cancel</button>
          </div>
        </div>
      )}

      {sendConfirmStep === 'idle' && sendResult && !sendResult.dryRun && (
        <div style={{ padding: '0.75rem', background: 'var(--success-light)', border: '1px solid var(--success)', borderRadius: 0, marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, margin: 0, color: 'var(--success)' }}>
            ✓ Sent to Instantly · added {sendResult.added} · skipped at parse {sendResult.skippedAtParse} · skipped by Instantly {sendResult.skippedByInstantly}
          </p>
          <p style={{ color: 'var(--success)', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
            Campaign: {sendResult.campaignName} ({sendResult.campaignId})
          </p>
        </div>
      )}

      {/* Preview table */}
      <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', margin: 0 }}>Preview of generated Email 1 (D0) per contact. Click a row to expand the body.</p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Instantly-ready 9-column CSV (email_angle = personalization) — upload straight to a campaign. */}
          <a href={`/api/${brand}/outreach/download/${today}-instantly.csv`} download
            style={{ background: 'var(--accent)', color: '#fff', borderRadius: 0, padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>
            ⬇ Download for Instantly
          </a>
          <button onClick={onReload} style={{ ...secondaryBtn(), padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>Reload</button>
        </div>
      </div>
      <div style={{ marginTop: '0.5rem', maxHeight: '480px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-primary)' }}>
        {loading && <p style={{ padding: '1rem', color: 'var(--text-tertiary)' }}>Loading emails...</p>}
        {!loading && emails && emails.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0 }}>
              <tr>
                <th style={apolloThStyle}>Name</th>
                <th style={apolloThStyle}>Company</th>
                <th style={apolloThStyle}>Subject</th>
                <th style={apolloThStyle}>Email 1 preview</th>
                <th style={apolloThStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e, i) => {
                const isSkip = !!(e['Skip Reason'] && e['Skip Reason'].length);
                // Use email as the stable key when present (survives Reload + reorder).
                // Fall back to index for skip-rows that may share an empty email column.
                return (
                  <tr key={e.Email || `row-${i}`} style={{ borderTop: '1px solid var(--border)', opacity: isSkip ? 0.5 : 1 }}>
                    <td style={previewTdStyle}>{e.Name}</td>
                    <td style={previewTdStyle}>{e.Company ?? '—'}</td>
                    <td style={previewTdStyle}>{e['Subject Line'] ?? '—'}</td>
                    <td style={{ ...previewTdStyle, maxWidth: '400px' }}>
                      <details><summary style={{ cursor: 'pointer' }}>{(e['Email 1 Body'] || '').slice(0, 120)}{(e['Email 1 Body'] || '').length > 120 ? '…' : ''}</summary><pre style={examplePreStyle}>{e['Email 1 Body']}</pre></details>
                    </td>
                    <td style={previewTdStyle}>{isSkip ? `SKIP: ${e['Skip Reason']}` : 'ready'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && (!emails || emails.length === 0) && <p style={{ padding: '1rem', color: 'var(--text-tertiary)' }}>No emails yet.</p>}
      </div>
    </section>
  );
}

// ── Style helpers ────────────────────────────────────────────────────────

const sectionBoxStyle: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 0, padding: '1.5rem',
  marginBottom: '2rem', background: 'var(--bg-secondary)',
};

const examplePreStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem',
  background: 'var(--bg-primary)', padding: '0.75rem', borderRadius: 0,
  marginTop: '0.5rem', border: '1px solid var(--border)', color: 'var(--text-secondary)',
};

const apolloThStyle: React.CSSProperties = {
  textAlign: 'left', padding: '0.5rem', fontWeight: 600, fontSize: '0.8rem',
  color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em',
};

const previewTdStyle: React.CSSProperties = { padding: '0.4rem 0.5rem', verticalAlign: 'top' };

function primaryBtn(): React.CSSProperties {
  return { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 0, padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' };
}
function secondaryBtn(): React.CSSProperties {
  return { background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 0, padding: '0.5rem 1rem', fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem' };
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '0.6rem 1.5rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
      border: 'none', background: 'none', color: active ? 'var(--accent)' : 'var(--text-tertiary)',
      borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: '-2px',
    }}>{children}</button>
  );
}
function SubTabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '0.5rem 1rem', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer',
      border: 'none', background: 'none', color: active ? 'var(--accent)' : 'var(--text-tertiary)',
      borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: '-1px',
    }}>{children}</button>
  );
}

function StatusPill({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 0, padding: '0.5rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 600, color: color || 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
